import React, { useState, useEffect } from 'react';
import { Box, Container, Card, Button, Typography, CircularProgress, Grid, Chip, Divider } from '@mui/material';
import { galleryService } from '../services/api';
import { ArrowBack, Image, VideoLibrary, PlayArrow } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { getAssetUrl } from '../config';

const GalleryDetailPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [galleryItem, setGalleryItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedMediaIndex, setSelectedMediaIndex] = useState(0);
  const [playingVideo, setPlayingVideo] = useState(false);

  useEffect(() => {
    fetchGalleryItem();
  }, [id]);

  const fetchGalleryItem = async () => {
    try {
      setLoading(true);
      const res = await galleryService.getGalleryById(id);
      const data = res.data?.data || res.data;
      setGalleryItem(data);
      setSelectedMediaIndex(0);
    } catch (error) {
      console.error('Error fetching gallery item:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!galleryItem) {
    return (
      <Box sx={{ backgroundColor: '#f5f7fa', minHeight: '100%', p: 4 }}>
        <Container maxWidth={false}>
          <Button 
            startIcon={<ArrowBack />}
            onClick={() => navigate('/admin/gallery')}
            sx={{ color: '#2c5f2d', mb: 4 }}
          >
            Back to Gallery
          </Button>
          <Typography variant="h5" color="error">Gallery item not found</Typography>
        </Container>
      </Box>
    );
  }

  const mediaItems = galleryItem.media || [];
  const currentMedia = mediaItems[selectedMediaIndex];
  const isVideo = currentMedia?.media_type === 'video';

  return (
    <Box sx={{ backgroundColor: '#f5f7fa', minHeight: '100%', p: 3 }}>
      <Container maxWidth={false}>
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Button 
            startIcon={<ArrowBack />}
            onClick={() => navigate('/admin/gallery')}
            sx={{ color: '#2c5f2d', mb: 2, textTransform: 'none' }}
          >
            Back to Gallery
          </Button>
          <Typography variant="h3" sx={{ fontWeight: 'bold', color: '#1a202c', mb: 1 }}>
            {galleryItem.title}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <Chip 
              label={galleryItem.category || 'Uncategorized'} 
              color="primary" 
              variant="outlined"
            />
            {galleryItem.campaign_id && (
              <Chip 
                label={`Campaign ID: ${galleryItem.campaign_id}`} 
                variant="outlined"
              />
            )}
            <Chip 
              label={`${mediaItems.length} media item${mediaItems.length !== 1 ? 's' : ''}`} 
              variant="outlined"
            />
          </Box>
        </Box>

        {/* Main Content */}
        <Grid container spacing={2}>
          {/* Main Media Display */}
          <Grid item xs={12} md={8}>
            <Card sx={{ p: 0, border: '1px solid #e8ecf1', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
              {currentMedia && (
                <Box>
                  {/* Media Container */}
                  <Box sx={{ 
                    position: 'relative',
                    backgroundColor: '#000',
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '320px'
                  }}>
                    {isVideo ? (
                      <>
                        <iframe
                          width="100%"
                          height="500"
                          src={getAssetUrl(currentMedia.media_url)}
                          title={galleryItem.title}
                          frameBorder="0"
                          allowFullScreen
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          style={{ display: playingVideo ? 'block' : 'block' }}
                        ></iframe>
                      </>
                    ) : (
                      <img 
                        src={getAssetUrl(currentMedia.media_url)} 
                        alt={galleryItem.title}
                        style={{
                          maxWidth: '100%',
                          maxHeight: '500px',
                          objectFit: 'contain'
                        }}
                      />
                    )}
                  </Box>

                  {/* Media Info */}
                  <Box sx={{ p: 2, borderTop: '1px solid #e8ecf1' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      {isVideo ? (
                        <VideoLibrary sx={{ color: '#7c3aed' }} />
                      ) : (
                        <Image sx={{ color: '#2c5f2d' }} />
                      )}
                      <Chip 
                        label={isVideo ? 'VIDEO' : 'IMAGE'} 
                        size="small"
                        color={isVideo ? 'secondary' : 'primary'}
                      />
                    </Box>
                    <Typography variant="caption" sx={{ color: '#667085', display: 'block', wordBreak: 'break-all' }}>
                      {currentMedia.media_url}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Card>

            {/* Media Gallery Thumbnails - Moved here */}
            {mediaItems.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: '600', color: '#1a202c', mb: 2 }}>
                  All Media ({mediaItems.length})
                </Typography>
                <Grid container spacing={2}>
                  {mediaItems.map((media, index) => (
                    <Grid item xs={6} sm={4} md={3} lg={2.4} key={index}>
                      <Card
                        sx={{
                          cursor: 'pointer',
                          border: selectedMediaIndex === index ? '3px solid #2c5f2d' : '1px solid #e8ecf1',
                          overflow: 'hidden',
                          transition: 'all 0.3s',
                          '&:hover': {
                            boxShadow: '0 8px 16px rgba(0,0,0,0.12)',
                            transform: 'translateY(-4px)'
                          }
                        }}
                        onClick={() => setSelectedMediaIndex(index)}
                      >
                        <Box sx={{ 
                          position: 'relative',
                          paddingBottom: '100%',
                          overflow: 'hidden',
                          backgroundColor: '#f0f0f0'
                        }}>
                          {media.media_type === 'image' ? (
                            <img
                              src={getAssetUrl(media.media_url)}
                              alt={`Media ${index + 1}`}
                              style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover'
                              }}
                            />
                          ) : (
                            <Box sx={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              width: '100%',
                              height: '100%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: '#1a1a1a'
                            }}>
                              <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '48px',
                                height: '48px',
                                backgroundColor: '#2c5f2d',
                                borderRadius: '50%'
                              }}>
                                <PlayArrow sx={{ color: '#fff', fontSize: '28px', ml: '4px' }} />
                              </Box>
                            </Box>
                          )}
                          {/* Badge */}
                          <Box sx={{
                            position: 'absolute',
                            top: 6,
                            right: 6,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            backgroundColor: 'rgba(0,0,0,0.7)',
                            color: '#fff',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontSize: '12px',
                            fontWeight: '600'
                          }}>
                            {media.media_type === 'image' ? (
                              <><Image sx={{ fontSize: '14px' }} /> Image</>
                            ) : (
                              <><VideoLibrary sx={{ fontSize: '14px' }} /> Video</>
                            )}
                          </Box>
                        </Box>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

            {/* Media Summary */}
            <Card sx={{ p: 3, mb: 2, mt: 3, border: '1px solid #e8ecf1', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
              <Typography variant="h6" sx={{ fontWeight: '600', color: '#1a202c', mb: 2 }}>
                Media Summary
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                <Box sx={{ p: 2, backgroundColor: '#f8fafb', borderRadius: 1, textAlign: 'center' }}>
                  <Typography variant="caption" sx={{ color: '#667085', fontWeight: '600' }}>
                    IMAGES
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#059669', mt: 0.5 }}>
                    {mediaItems.filter(m => m.media_type === 'image').length}
                  </Typography>
                </Box>
                <Box sx={{ p: 2, backgroundColor: '#f8fafb', borderRadius: 1, textAlign: 'center' }}>
                  <Typography variant="caption" sx={{ color: '#667085', fontWeight: '600' }}>
                    VIDEOS
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#7c3aed', mt: 0.5 }}>
                    {mediaItems.filter(m => m.media_type === 'video').length}
                  </Typography>
                </Box>
              </Box>
            </Card>
          </Grid>

          {/* Details Sidebar */}
          <Grid item xs={12} md={4}>
            {/* Description */}
            <Card sx={{ p: 3, mb: 2, border: '1px solid #e8ecf1', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
              <Typography variant="h6" sx={{ fontWeight: '600', color: '#1a202c', mb: 2 }}>
                Description
              </Typography>
              <Typography variant="body2" sx={{ color: '#667085', lineHeight: 1.6 }}>
                {galleryItem.description || 'No description provided'}
              </Typography>
            </Card>

            {/* Details */}
            <Card sx={{ p: 3, mb: 2, border: '1px solid #e8ecf1', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
              <Typography variant="h6" sx={{ fontWeight: '600', color: '#1a202c', mb: 2 }}>
                Gallery Information
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box>
                  <Typography variant="caption" sx={{ color: '#667085', fontWeight: '600' }}>
                    GALLERY ID
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#1a202c', mt: 0.5, fontFamily: 'monospace' }}>
                    {galleryItem.id}
                  </Typography>
                </Box>
                <Divider />
                <Box>
                  <Typography variant="caption" sx={{ color: '#667085', fontWeight: '600' }}>
                    CATEGORY
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#1a202c', mt: 0.5 }}>
                    {galleryItem.category || 'Uncategorized'}
                  </Typography>
                </Box>
                <Divider />
                <Box>
                  <Typography variant="caption" sx={{ color: '#667085', fontWeight: '600' }}>
                    TOTAL MEDIA ITEMS
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#1a202c', mt: 0.5 }}>
                    {mediaItems.length} item{mediaItems.length !== 1 ? 's' : ''}
                  </Typography>
                </Box>
                <Divider />
                <Box>
                  <Typography variant="caption" sx={{ color: '#667085', fontWeight: '600' }}>
                    DISPLAY ORDER
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#1a202c', mt: 0.5 }}>
                    {galleryItem.display_order || 'N/A'}
                  </Typography>
                </Box>
              </Box>
            </Card>

            {/* Campaign Association */}
            {galleryItem.campaign_id && (
              <Card sx={{ p: 3, mb: 2, border: '2px solid #2c5f2d', backgroundColor: '#f0f9ff', boxShadow: '0 4px 12px rgba(44, 95, 45, 0.1)' }}>
                <Typography variant="h6" sx={{ fontWeight: '600', color: '#2c5f2d', mb: 2 }}>
                  Campaign Association
                </Typography>
                <Box>
                  <Typography variant="caption" sx={{ color: '#667085', fontWeight: '600' }}>
                    CAMPAIGN ID
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#1a202c', mt: 0.5, fontFamily: 'monospace' }}>
                    {galleryItem.campaign_id}
                  </Typography>
                </Box>
              </Card>
            )}

            {/* Metadata Card */}
            <Card sx={{ p: 3, border: '1px solid #e8ecf1', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
              <Typography variant="h6" sx={{ fontWeight: '600', color: '#1a202c', mb: 2 }}>
                Metadata
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box>
                  <Typography variant="caption" sx={{ color: '#667085', fontWeight: '600' }}>
                    CREATED DATE
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#1a202c', mt: 0.5 }}>
                    {new Date(galleryItem.created_at).toLocaleDateString('en-US', { 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric'
                    })}
                  </Typography>
                </Box>
                <Divider />
                <Box>
                  <Typography variant="caption" sx={{ color: '#667085', fontWeight: '600' }}>
                    CREATED AT (WITH TIME)
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#1a202c', mt: 0.5 }}>
                    {new Date(galleryItem.created_at).toLocaleDateString('en-US', { 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </Typography>
                </Box>
              </Box>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default GalleryDetailPage;
